import type { BrandPalette } from './types';

export const SEMILLA_PRIMARIO = '#3B82F6';
export const SEMILLA_SECUNDARIO = '#1E40AF';
export const SEMILLA_ACENTO = '#F59E0B';
const CERCA_NEGRO = '#1A1A1A';
const BLANCO = '#FFFFFF';

export function normalizarHex(hex?: string | null): string {
  if (!hex) return '';
  let h = hex.trim().toLowerCase();
  if (!h.startsWith('#')) h = `#${h}`;
  if (/^#[0-9a-f]{3}$/.test(h)) {
    h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
  }
  if (!/^#[0-9a-f]{6}$/.test(h)) return '';
  return h.toUpperCase();
}

export function esSemilla(hex: string | undefined, semilla: string): boolean {
  const n = normalizarHex(hex);
  return !n || n === normalizarHex(semilla);
}

function rgb(hex: string): { r: number; g: number; b: number } {
  const h = normalizarHex(hex).slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function aHex(r: number, g: number, b: number): string {
  const c = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase();
}

export function luminanciaRelativa(hex: string): number {
  const { r, g, b } = rgb(hex);
  const f = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function contraste(a: string, b: string): number {
  const L1 = luminanciaRelativa(a);
  const L2 = luminanciaRelativa(b);
  const hi = Math.max(L1, L2);
  const lo = Math.min(L1, L2);
  return (hi + 0.05) / (lo + 0.05);
}

/** White or near-black, whichever contrasts more with the background. */
export function colorSobre(fondo: string): '#FFFFFF' | '#1A1A1A' {
  return contraste(fondo, BLANCO) >= contraste(fondo, CERCA_NEGRO) ? BLANCO : CERCA_NEGRO;
}

function aHsl(hex: string): { h: number; s: number; l: number } {
  const { r, g, b } = rgb(hex);
  const R = r / 255;
  const G = g / 255;
  const B = b / 255;
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === R) h = (G - B) / d + (G < B ? 6 : 0);
  else if (max === G) h = (B - R) / d + 2;
  else h = (R - G) / d + 4;
  return { h: h * 60, s, l };
}

function deHsl(h: number, s: number, l: number): string {
  const H = ((h % 360) + 360) % 360;
  const hue = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  if (s === 0) {
    const v = l * 255;
    return aHex(v, v, v);
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hk = H / 360;
  return aHex(hue(p, q, hk + 1 / 3) * 255, hue(p, q, hk) * 255, hue(p, q, hk - 1 / 3) * 255);
}

function mezclar(a: string, b: string, cantidadB: number): string {
  const A = rgb(a);
  const B = rgb(b);
  const t = cantidadB;
  return aHex(A.r + (B.r - A.r) * t, A.g + (B.g - A.g) * t, A.b + (B.b - A.b) * t);
}

function oscurecer(hex: string): string {
  const { h, s, l } = aHsl(hex);
  return deHsl(h, Math.min(1, s), Math.min(l * 0.55, 0.32));
}

function tinteContraste(hex: string): string {
  const { h, s } = aHsl(hex);
  return deHsl(h + 42, Math.max(0.55, s), 0.55);
}

export interface ColoresMarca {
  primario?: string;
  secundario?: string;
  acento?: string;
}

/**
 * Builds the template CSS variables from the colors saved in Configuración.
 * Seed defaults are treated as unset so a shop that never picked colors
 * does not get the Clientelo blue painted onto every post.
 */
export function paletaDesdeColores(colores: ColoresMarca | undefined, forzar = false): BrandPalette | null {
  const primarioRaw = colores?.primario;
  const secundarioRaw = colores?.secundario;
  const acentoRaw = colores?.acento;
  const primarioEsSemilla = esSemilla(primarioRaw, SEMILLA_PRIMARIO);
  const secundarioEsSemilla = esSemilla(secundarioRaw, SEMILLA_SECUNDARIO);
  const acentoEsSemilla = esSemilla(acentoRaw, SEMILLA_ACENTO);
  if (!forzar && primarioEsSemilla && secundarioEsSemilla && acentoEsSemilla) return null;

  const primario = normalizarHex(primarioRaw) || SEMILLA_PRIMARIO;
  const secundario = secundarioEsSemilla ? oscurecer(primario) : normalizarHex(secundarioRaw);
  const acento = acentoEsSemilla ? tinteContraste(primario) : normalizarHex(acentoRaw);
  const bg = mezclar(primario, BLANCO, 0.92);
  const text = luminanciaRelativa(bg) < 0.45 ? '#F7F7F5' : CERCA_NEGRO;

  return {
    '--brand-primary': primario,
    '--brand-secondary': secundario,
    '--brand-accent': acento,
    '--brand-bg': bg,
    '--brand-text': text,
    '--brand-on-primary': colorSobre(primario),
    '--brand-on-secondary': colorSobre(secundario),
    '--brand-on-accent': colorSobre(acento),
  };
}

/** Role rotations of the merchant's own colors. The first entry is Usar mi marca. */
export function combinacionesMarca(colores: ColoresMarca | undefined): BrandPalette[] {
  const base = paletaDesdeColores(colores);
  if (!base) return [];
  const roles: Array<['--brand-primary' | '--brand-secondary' | '--brand-accent', '--brand-primary' | '--brand-secondary' | '--brand-accent', '--brand-primary' | '--brand-secondary' | '--brand-accent']> = [
    ['--brand-primary', '--brand-secondary', '--brand-accent'],
    ['--brand-secondary', '--brand-accent', '--brand-primary'],
    ['--brand-accent', '--brand-primary', '--brand-secondary'],
  ];
  const vistos = new Set<string>();
  const out: BrandPalette[] = [];
  for (const [p, s, a] of roles) {
    const next = paletaDesdeColores(
      { primario: base[p], secundario: base[s], acento: base[a] },
      true
    );
    if (!next) continue;
    const key = `${next['--brand-primary']}|${next['--brand-secondary']}|${next['--brand-accent']}`;
    if (vistos.has(key)) continue;
    vistos.add(key);
    out.push(next);
  }
  return out;
}
